import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArticleCard } from '../components/ArticleCard';
import { articlesAPI } from '../services/api';

export const ArticleDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      const articleData = await articlesAPI.getBySlug(slug);
      setArticle(articleData);
      
      // Load related articles from same category
      const allArticles = await articlesAPI.getAll({ category: articleData.category });
      const related = allArticles
        .filter(a => a.slug !== slug)
        .slice(0, 3);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Error loading article:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando artículo...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return <Navigate to="/" replace />;
  }

  const formattedDate = new Date(article.date).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/">
            <Button variant="ghost" className="hover:text-teal-600 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al inicio
            </Button>
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Badge */}
        <Link to={`/categoria/${article.category}`}>
          <Badge className="bg-teal-500 hover:bg-teal-600 mb-4 cursor-pointer">
            {article.category_name}
          </Badge>
        </Link>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {article.title}
        </h1>

        {/* Excerpt */}
        <p className="text-xl text-gray-600 mb-6 leading-relaxed">
          {article.excerpt}
        </p>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
              <span className="text-teal-700 font-semibold">
                {article.author.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <span className="font-medium">{article.author}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Calendar className="h-4 w-4" />
            <span>{formattedDate}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className="h-4 w-4" />
            <span>{article.read_time} de lectura</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto hover:text-teal-600 transition-colors"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Compartir
          </Button>
        </div>

        {/* Featured Image */}
        <div className="relative h-96 rounded-xl overflow-hidden mb-12 shadow-lg">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Content */}
        <div
          className="prose prose-lg max-w-none mb-12"
          dangerouslySetInnerHTML={{ __html: article.content }}
          style={{
            lineHeight: '1.8',
            fontSize: '1.125rem',
            color: '#1f2937'
          }}
        />

        <Separator className="my-8" />

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center space-x-2 mb-4">
              <Tag className="h-5 w-5 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-700 uppercase">Etiquetas</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="bg-gray-50 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300 transition-colors cursor-pointer"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Author Card */}
        <div className="bg-teal-50 rounded-xl p-6 mb-12">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">
                {article.author.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                Escrito por {article.author}
              </h3>
              <p className="text-gray-600 text-sm">
                Experto en estrategias de monetización online y creación de negocios digitales.
                Comparte conocimientos prácticos para ayudarte a generar ingresos en internet.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Artículos Relacionados
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <ArticleCard key={relatedArticle.id} article={relatedArticle} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
